
import { MessageCircle, Clock, CheckCircle2, ArrowLeft, ArrowUpRight } from "lucide-react";

const SUPPORT_WHATSAPP_LINK = `https://wa.me/15557482413?text=${encodeURIComponent(
  "Hello, I need assistance with my account activation."
)}`;
const InactivePage = () => {
  const steps = [
    {
      icon: CheckCircle2,
      // label: "Registration received",
      description: "Your details have been saved successfully and are awaiting review.",
      status: "done",
    },
    {
      icon: Clock,
      // label: "Under review",
      description: "Our team will review your account and reach out to you within 24 hours.",
      status: "active",
    },
    {
      icon: MessageCircle,
      label: "Contact support if you need immediate assistance.",
      description: null,
      status: "pending",
    },
  ];

  const statusStyles = {
    done:    { icon: "text-emerald-600", bg: "bg-emerald-50",  row: "bg-gray-50 border-gray-100" },
    active:  { icon: "text-amber-500",   bg: "bg-amber-50",    row: "bg-gray-50 border-gray-100" },
    pending: { icon: "text-gray-400",    bg: "bg-gray-100",    row: "bg-gray-50 border-gray-100" },
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-[420px]">

        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="h-[3px] bg-gradient-to-r from-emerald-500 to-emerald-300" />

          <div className="px-7 pt-8 pb-7">

            {/* Icon + badge */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative w-16 h-16 mb-3 animate-[float_3s_ease-in-out_infinite]">
                <div className="w-full h-full rounded-2xl bg-amber-50 flex items-center justify-center">
                  <Clock size={28} className="text-amber-500" />
                </div>
                <span className="absolute -top-[3px] -right-[3px] w-3.5 h-3.5 rounded-full bg-amber-400 border-2 border-white animate-ping opacity-75" />
                <span className="absolute -top-[3px] -right-[3px] w-3.5 h-3.5 rounded-full bg-amber-400 border-2 border-white" />
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-700 text-[12px] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                Pending activation
              </span>
            </div>

            <h1 className="text-[20px] font-bold text-gray-900 text-center mb-2 leading-snug">
              Account pending activation
            </h1>
            <p className="text-sm text-gray-500 text-center leading-relaxed mb-6">
              Your account was created successfully. Our team needs to verify
              and activate it before sign in.
            </p>

            {/* Steps */}
            <div className="flex flex-col gap-2 mb-6">
              {steps.map(({ icon: Icon, label, description, status }) => {
                const s = statusStyles[status];
                return (
                  <div key={label} className={`flex items-start gap-3 p-3 rounded-xl border ${s.row}`}>
                    <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${s.bg}`}>
                      <Icon size={15} className={s.icon} />
                    </div>
                    <div className="flex flex-col justify-center">
                      <p className={`text-[13px] leading-snug ${description ? "font-semibold text-gray-800 mb-0.5" : " text-gray-500"}`}>
                        {label}
                      </p>
                      {description && (
                        <p className="text-[13px] text-gray-500 leading-snug">{description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* WhatsApp CTA */}
            <a
              href={SUPPORT_WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="w-full flex items-center justify-center gap-2.5 py-3.5 px-5 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white text-sm font-semibold rounded-xl transition-all duration-150 mb-2.5"
            >
              <svg viewBox="0 0 32 32" fill="currentColor" className="w-[18px] h-[18px] flex-shrink-0">
                <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.472 2.025 7.773L0 32l8.437-2.013A15.94 15.94 0 0016 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.28 13.28 0 01-6.773-1.853l-.486-.29-5.007 1.195 1.22-4.876-.317-.5A13.278 13.278 0 012.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.27-9.87c-.398-.2-2.355-1.163-2.72-1.295-.366-.133-.632-.2-.898.2-.266.398-1.03 1.295-1.263 1.561-.232.266-.465.3-.863.1-.398-.2-1.681-.62-3.203-1.978-1.184-1.057-1.983-2.363-2.216-2.761-.232-.398-.025-.613.175-.812.18-.179.398-.465.598-.698.2-.232.266-.398.398-.664.133-.266.066-.498-.033-.698-.1-.2-.898-2.163-1.23-2.961-.324-.778-.654-.672-.898-.684l-.765-.013c-.266 0-.698.1-1.064.498-.366.398-1.397 1.363-1.397 3.326s1.43 3.86 1.63 4.127c.199.266 2.814 4.296 6.818 6.027.953.412 1.696.657 2.276.841.956.304 1.826.261 2.514.158.767-.114 2.355-.963 2.688-1.893.332-.93.332-1.727.232-1.893-.1-.166-.366-.266-.764-.465z" />
              </svg>
              Contact support on WhatsApp
              <ArrowUpRight size={14} className="ml-auto" />
            </a>

            {/* Back to login */}
            <a
              href="/login"
              className="w-full flex items-center justify-center gap-2 py-3 px-5 bg-white hover:bg-gray-50 active:scale-[0.98] text-gray-600 hover:text-gray-900 text-sm font-medium rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-150"
            >
              <ArrowLeft size={15} />
              Back to login
            </a>

          </div>
        </div>

      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
};

export default InactivePage;